import {
  getNutrientDefinition,
  resolveNutrient,
  type NutrientDefinition,
} from "./nutrient-catalog";
import {
  convertNutrientAmount,
  isMassNutrientUnit,
  normalizeNutrientUnit,
  NUTRIENT_KEY_MAX_LENGTH,
  NUTRIENT_NAME_MAX_LENGTH,
  type MicronutrientAmount,
  type Micronutrients,
} from "./nutrients";

interface CatalogCandidate {
  readonly originalKey: string;
  readonly definition: NutrientDefinition;
  readonly amount: MicronutrientAmount;
  readonly priority: number;
}

/**
 * Correlates historical/provider spellings to catalog IDs without discarding
 * unsupported legacy data. Exact catalog keys win if a record contains both a
 * canonical key and one of its aliases, avoiding accidental double-counting.
 *
 * IU values remain in a separate legacy dimension. Their conversion depends on
 * the particular nutrient and chemical form, so a generic mass conversion
 * would create false precision.
 */
export function normalizeKnownMicronutrients(input: Micronutrients): Micronutrients {
  const result: Record<string, MicronutrientAmount> = {};
  const candidates: CatalogCandidate[] = [];

  for (const [originalKey, amount] of Object.entries(input)) {
    const unit = normalizeNutrientUnit(amount.unit);
    const definition = resolveNutrient(originalKey) ?? resolveNutrient(amount.name ?? "");

    if (!definition && isBetaCarotene(originalKey, amount.name) && isMassNutrientUnit(unit)) {
      const vitaminA = getNutrientDefinition("vitamin-a")!;
      const preferred = amountInPreferredUnit(vitaminA, { ...amount, unit });
      candidates.push({
        originalKey,
        definition: vitaminA,
        amount: { ...preferred, amount: preferred.amount / 6 },
        priority: 0,
      });
      continue;
    }

    if (!definition || !isMassNutrientUnit(unit)) {
      const desiredLegacyKey = definition && originalKey === definition.id
        ? `${originalKey}-iu`
        : originalKey;
      const legacyKey = uniqueNutrientKey(desiredLegacyKey, result);
      result[legacyKey] = { ...amount, unit };
      continue;
    }

    candidates.push({
      originalKey,
      definition,
      amount: { ...amount, unit },
      priority: originalKey === definition.id ? 2 : 1,
    });
  }

  candidates.sort((left, right) =>
    right.priority - left.priority ||
    left.originalKey.localeCompare(right.originalKey),
  );

  for (const candidate of candidates) {
    const normalized = amountInPreferredUnit(candidate.definition, candidate.amount);
    const existing = result[candidate.definition.id];
    if (!existing) {
      result[candidate.definition.id] = normalized;
      continue;
    }
    if (!sameAmount(existing, normalized)) {
      const conflictKey = uniqueLegacyConflictKey(candidate.originalKey, result);
      const originalName = candidate.amount.name?.trim() || candidate.definition.name;
      result[conflictKey] = {
        ...candidate.amount,
        name: `Legacy conflict: ${originalName}`.slice(0, NUTRIENT_NAME_MAX_LENGTH),
      };
    }
  }

  return result;
}

function isBetaCarotene(key: string, name?: string): boolean {
  return [key, name ?? ""].some((value) =>
    value.normalize("NFKC").trim().toLocaleLowerCase("en-AU").replace(/[_\s]+/g, "-") === "beta-carotene",
  );
}

function sameAmount(left: MicronutrientAmount, right: MicronutrientAmount): boolean {
  if (left.unit !== right.unit) return false;
  const tolerance = Math.max(1e-9, Math.max(Math.abs(left.amount), Math.abs(right.amount)) * 1e-9);
  return Math.abs(left.amount - right.amount) <= tolerance;
}

function uniqueLegacyConflictKey(
  originalKey: string,
  existing: Readonly<Record<string, MicronutrientAmount>>,
): string {
  const prefix = "legacy-conflict-";
  const stem = originalKey
    .normalize("NFKC")
    .toLocaleLowerCase("en-AU")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "nutrient";
  const base = `${prefix}${stem}`.slice(0, NUTRIENT_KEY_MAX_LENGTH);
  return uniqueNutrientKey(base, existing);
}

function uniqueNutrientKey(
  desiredKey: string,
  existing: Readonly<Record<string, MicronutrientAmount>>,
): string {
  const base = desiredKey.slice(0, NUTRIENT_KEY_MAX_LENGTH);
  let key = base;
  let counter = 2;
  while (Object.prototype.hasOwnProperty.call(existing, key)) {
    const suffix = `-${counter++}`;
    key = `${base.slice(0, NUTRIENT_KEY_MAX_LENGTH - suffix.length)}${suffix}`;
  }
  return key;
}

/** Provider boundary: retain only values confidently resolved to the catalog. */
export function catalogMicronutrientsOnly(input: Micronutrients): Micronutrients {
  return Object.fromEntries(
    Object.entries(normalizeKnownMicronutrients(input))
      .filter(([key]) => getNutrientDefinition(key) !== undefined),
  );
}

export function amountInPreferredUnit(
  nutrient: NutrientDefinition | string,
  value: MicronutrientAmount,
): MicronutrientAmount {
  const definition = typeof nutrient === "string" ? getNutrientDefinition(nutrient) : nutrient;
  if (!definition) throw new Error(`Unknown vitamin or mineral: ${nutrient}`);
  const unit = normalizeNutrientUnit(value.unit);
  if (!isMassNutrientUnit(unit)) {
    throw new Error(`${definition.name} must be recorded in a mass unit`);
  }
  return {
    amount: convertNutrientAmount(value.amount, unit, definition.unit),
    unit: definition.unit,
    name: definition.name,
  };
}
